import {
  Component,
  OnInit,
  OnDestroy,
  signal,
  computed,
  inject,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatDateTime } from '@app/shared/utils/date-formatter.util';
import { PermissionService } from '@app/core/services/permission.service';
import type { TableColumn } from '@shared/components/data-table/data-table.component';
import { AdvancedDataTableComponent } from '@shared/components/advanced-data-table/advanced-data-table.component';
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';
import {
  SearchFormComponent,
  FILTER_ACTIVE_PARAM,
} from '@shared/components/search-form/search-form.component';
import {
  StatusFilterComponent,
  StatusOption,
  ActionButton,
} from '@shared/components/status-filter/status-filter.component';
import {
  ConfirmationDialogComponent,
  ConfirmationButton,
} from '@shared/components/confirmation-dialog/confirmation-dialog.component';
import {
  MATERIAL_TABLE_COLUMNS,
  MATERIAL_DEFAULT_COLUMNS_ORDER,
  MATERIAL_FLEXIBLE_COLUMNS_ORDER,
  MATERIAL_ACTION_COLUMN,
  MATERIAL_SEARCH_CONFIG,
} from '../material.config';
import {
  MaterialService,
  MaterialSearchParams,
} from '../services/material.service';
import { MaterialItem } from '../material.config';

@Component({
  selector: 'app-material-list',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    TagModule,
    TooltipModule,
    ToastModule,
    TranslateModule,
    AdvancedDataTableComponent,
    AppButtonComponent,
    SearchFormComponent,
    StatusFilterComponent,
    ConfirmationDialogComponent,
  ],
  providers: [MessageService],
  templateUrl: './material-list.component.html',
  styleUrls: ['./material-list.component.css'],
})
export class MaterialListComponent implements OnInit, OnDestroy {
  // Search configuration
  searchConfig = MATERIAL_SEARCH_CONFIG;

  // Data management
  materials = signal<MaterialItem[]>([]);
  filteredMaterials = signal<MaterialItem[]>([]);
  loading = signal(false);
  totalRecords = signal(0);

  // Pagination
  currentPage = signal(1);
  pageSize = signal(20);

  // Active search filter, so reloads after mutations (delete, etc.) keep it applied
  private currentSearchParams: MaterialSearchParams | null = null;

  // Selection
  selectedMaterials = signal<MaterialItem[]>([]);

  // Confirmation dialog properties
  showConfirmationDialog = false;
  selectedMaterial: MaterialItem | null = null;
  dialogTitle = '';
  dialogMessage = '';
  dialogButtons: ConfirmationButton[] = [];
  currentAction: 'delete' | 'batchDelete' | null = null;

  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Permission flags - computed from PermissionService
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product_asset.view');
  });
  canCreate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product_asset.create');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product_asset.edit');
  });
  canDelete = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product_asset.delete');
  });
  canApprove = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('product.product_asset.approve');
  });

  // Check if user has any action permissions (UPDATE or DELETE)
  hasAnyActionPermission = computed(() => this.canUpdate() || this.canDelete());

  // Advanced table column configuration
  defaultColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      MATERIAL_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];
    for (const key of MATERIAL_DEFAULT_COLUMNS_ORDER) {
      const col = fromConfig(key);
      if (col) cols.push(col);
    }

    // Conditionally add actions column at the end if permitted
    if (this.hasAnyActionPermission()) {
      const actionsCol = fromConfig(MATERIAL_ACTION_COLUMN.ACTIONS);
      if (actionsCol) cols.push(actionsCol);
    }

    return cols;
  });

  flexibleColumns = computed((): TableColumn[] => {
    const fromConfig = (prop: string) =>
      MATERIAL_TABLE_COLUMNS.find((c) => c.prop === prop);

    const cols: TableColumn[] = [];

    for (const prop of MATERIAL_FLEXIBLE_COLUMNS_ORDER) {
      const col = fromConfig(prop);
      if (col) {
        cols.push(col);
      }
    }

    return cols;
  });

  // Status filter options
  statusOptions: StatusOption[] = [
    { key: 'all', label: 'material.status.all', active: true },
    { key: 'active', label: 'material.status.active' },
    { key: 'inactive', label: 'material.status.inactive' },
  ];

  // Button configuration base
  private readonly primaryButtonBase: ActionButton = {
    label: 'common.actions.new',
    severity: 'info',
    show: true,
    icon: 'add',
  };

  private readonly secondaryButtonBase: ActionButton = {
    label: 'common.actions.delete',
    severity: 'danger',
    show: true,
    disabled: true,
    icon: 'delete',
  };

  // Computed button configurations with permissions
  primaryButton = computed(() => ({
    ...this.primaryButtonBase,
    show: this.canCreate(),
  }));

  secondaryButton = computed(() => ({
    ...this.secondaryButtonBase,
    show: this.canDelete(),
    disabled: this.selectedMaterials().length === 0,
  }));

  // Column config button configuration (not needed for app-button, but keeping for consistency)
  // The button will be configured directly in the template

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private materialService: MaterialService,
    private messageService: MessageService,
    private translateService: TranslateService,
  ) {}

  ngOnInit(): void {
    const urlParams = this.route.snapshot.queryParams;
    const hasSearchParams = urlParams[FILTER_ACTIVE_PARAM] === 'true';

    if (!hasSearchParams) {
      this.loadMaterials();
    }

    // Subscribe to permissions loaded state to trigger change detection
    // This ensures computed signals re-evaluate when permissions are loaded
    this.permissionService.permissionsLoaded$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loaded) => {
        if (loaded) {
          // Trigger change detection to update computed signals
          this.cdr.detectChanges();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadMaterials(): void {
    this.loading.set(true);

    this.materialService
      .getMaterials(
        this.currentPage(),
        this.pageSize(),
        this.currentSearchParams ?? undefined,
      )
      .subscribe({
        next: (response) => {
          const materials = response?.data || [];
          const totalRecords = response?.totalRecords || 0;

          this.materials.set(materials);
          this.filteredMaterials.set(materials);
          this.totalRecords.set(totalRecords);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error loading product assets:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.interpolateMessage('common.actions.error'),
            detail: this.interpolateMessage(
              'material.messages.error_loading_product_assets',
            ),
          });
          this.loading.set(false);
        },
      });
  }

  onSearch(searchCriteria: any): void {
    this.loading.set(true);

    const searchParams: MaterialSearchParams = {
      materialName: searchCriteria.materialName,
      code: searchCriteria.code,
      page: 1,
      size: this.pageSize(),
    };
    this.currentSearchParams = searchParams;

    this.materialService
      .getMaterials(1, this.pageSize(), searchParams)
      .subscribe({
        next: (response) => {
          const materials = response?.data || [];
          const totalRecords = response?.totalRecords || 0;

          this.filteredMaterials.set(materials);
          this.totalRecords.set(totalRecords);
          this.currentPage.set(1);
          this.loading.set(false);
        },
        error: (error) => {
          console.error('Error searching product assets:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.interpolateMessage('common.actions.error'),
            detail: this.interpolateMessage(
              'material.messages.error_searching_product_assets',
            ),
          });
          this.loading.set(false);
        },
      });
  }

  onReset(): void {
    this.currentSearchParams = null;
    this.loadMaterials();
  }

  onPageChange(event: any): void {
    // Handle both page changes and row size changes
    this.currentPage.set(event.page + 1);
    this.pageSize.set(event.rows);
    this.loadMaterials();
  }

  onSelectionChange(selection: MaterialItem[]): void {
    this.selectedMaterials.set(selection);
    // secondaryButton is a computed signal, so it will update automatically
  }

  onAddMaterial(): void {
    this.router.navigate(['/goods/material/add'], {
      queryParams: this.route.snapshot.queryParams,
    });
  }

  onEditMaterial(material: MaterialItem): void {
    this.router.navigate(['/goods/material/edit'], {
      queryParams: {
        id: material.id,
        ...this.route.snapshot.queryParams,
      },
    });
  }

  onViewMaterial(material: MaterialItem): void {
    this.router.navigate(['/goods/material/view'], {
      queryParams: {
        id: material.id,
        ...this.route.snapshot.queryParams,
      },
    });
  }

  onDeleteMaterial(material: MaterialItem): void {
    this.selectedMaterial = material;
    this.dialogTitle = 'material.messages.confirm_delete_title';
    this.dialogMessage = this.interpolateMessage(
      'material.messages.confirm_delete_message',
      { name: material.materialName },
    );

    this.currentAction = 'delete';
    this.dialogButtons = [
      { label: 'material.messages.cancel', action: 'cancel', outlined: true },
      {
        label: 'material.messages.delete',
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  onDeleteSelected(): void {
    const selectedMaterials = this.selectedMaterials();

    if (selectedMaterials.length === 0) {
      const warningMessage = this.interpolateMessage(
        'material.messages.please_select_assets',
      );
      this.messageService.add({
        severity: 'warn',
        summary: 'common.actions.warning',
        detail: warningMessage,
      });
      return;
    }

    this.dialogTitle = 'material.messages.confirm_batch_delete_title';

    this.dialogMessage = this.interpolateMessage(
      'material.messages.confirm_batch_delete_message',
      { count: selectedMaterials.length },
    );
    this.currentAction = 'batchDelete';
    this.dialogButtons = [
      { label: 'material.messages.cancel', action: 'cancel', outlined: true },
      {
        label: 'material.messages.delete',
        action: 'confirm',
        severity: 'danger',
      },
    ];
    this.showConfirmationDialog = true;
  }

  private interpolateMessage(messageKey: string, params?: any): string {
    return this.translateService.instant(messageKey, params);
  }

  getImageUrl(imageData: any): string | null {
    if (imageData?.previewUrl) {
      return imageData.previewUrl;
    }
    return null;
  }

  // Confirmation dialog methods
  onConfirmatiButtonClick(action: string): void {
    if (action === 'cancel') {
      this.closeConfirmationDialog();
    } else if (action === 'confirm') {
      if (this.currentAction === 'delete') {
        this.confirmDelete();
      } else if (this.currentAction === 'batchDelete') {
        this.confirmBatchDelete();
      }
    }
  }

  confirmDelete(): void {
    if (this.selectedMaterial) {
      this.materialService.deleteMaterial(this.selectedMaterial.id).subscribe({
        next: (success) => {
          const successMessage = this.interpolateMessage(
            'material.messages.product_asset_deleted_successfully',
          );
          this.messageService.add({
            severity: 'success',
            summary: this.interpolateMessage('common.actions.success'),
            detail: successMessage,
          });
          this.loadMaterials();
        },
        error: (error) => {
          let msgContent = this.interpolateMessage(
            'material.messages.failed_to_delete_product_asset',
          );
          // Check for related products error in different possible error structures
          const errorMessage = error.error?.message || error.message || '';
          if (
            errorMessage.includes('has related products and cannot be deleted')
          ) {
            msgContent = this.interpolateMessage(
              'material.messages.failed_to_delete_product_asset_has_related_products',
            );
          }
          this.messageService.add({
            severity: 'error',
            summary: this.interpolateMessage('common.actions.error'),
            detail: msgContent,
          });
        },
      });
    }
    this.closeConfirmationDialog();
  }

  confirmBatchDelete(): void {
    const selectedIds = this.selectedMaterials().map((material) => material.id);

    this.materialService.batchDeleteMaterials(selectedIds).subscribe({
      next: (response) => {
        const successMessage = this.translateService.instant(
          'material.messages.product_assets_deleted_successfully',
        );
        this.messageService.add({
          severity: 'success',
          summary: this.interpolateMessage('common.actions.success'),
          detail: successMessage,
        });
        this.selectedMaterials.set([]);
        this.loadMaterials();
        // secondaryButton is a computed signal, so it will update automatically
      },
      error: (error) => {
        let msgContent = this.interpolateMessage(
          'material.messages.failed_to_delete_product_assets',
        );
        // Check for related products error in different possible error structures
        const errorMessage = error.error?.message || error.message || '';
        if (
          errorMessage.includes('has related products and cannot be deleted')
        ) {
          const blockedIdsMatch = errorMessage.match(/id\s*=\s*([\d,]+)/);
          const blockedIds = blockedIdsMatch ? blockedIdsMatch[1].split(',') : [];
          const blockedNames = this.selectedMaterials()
            .filter((material) => blockedIds.includes(material.id))
            .map((material) => material.materialName);
          msgContent = blockedNames.length
            ? this.interpolateMessage(
                'material.messages.failed_to_delete_product_assets_has_related_products_named',
                { names: blockedNames.join(', ') },
              )
            : this.interpolateMessage(
                'material.messages.failed_to_delete_product_assets_has_related_products',
              );
        }
        this.messageService.add({
          severity: 'error',
          summary: this.interpolateMessage('common.actions.error'),
          detail: msgContent,
        });
      },
    });
    this.closeConfirmationDialog();
  }

  closeConfirmationDialog(): void {
    this.showConfirmationDialog = false;
    this.selectedMaterial = null;
    this.currentAction = null;
    this.dialogTitle = '';
    this.dialogMessage = '';
    this.dialogButtons = [];
  }

  // Status methods
  getStatusSeverity(status: boolean): 'success' | 'danger' {
    return status ? 'success' : 'danger';
  }

  getStatusDisplayText(status: boolean): string {
    return status ? 'material.status.active' : 'material.status.inactive';
  }

  getStatusIcon(status: boolean): string {
    return status
      ? 'pi pi-check-circle text-green-500'
      : 'pi pi-times-circle text-red-500';
  }

  getStatusStyle(status: boolean): any {
    return status
      ? {
          'background-color': '#ecfdf5',
          color: '#047857',
          border: 'none',
        }
      : {
          'background-color': '#fef2f2',
          color: '#dc2626',
          border: 'none',
        };
  }

  // Utility method to strip HTML tags and decode entities for tooltip display
  stripHtmlTags(html: string): string {
    if (!html) return '';

    // Create a temporary div element to decode HTML entities
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Get the text content (this automatically decodes HTML entities)
    let textContent = tempDiv.textContent || tempDiv.innerText || '';

    // Clean up extra whitespace
    textContent = textContent.replace(/\s+/g, ' ').trim();

    return textContent;
  }

  /**
   * Format datetime using utility function
   */
  formatDateTime = formatDateTime;

  // Reference to advanced table component
  @ViewChild('advancedTable') advancedTable?: AdvancedDataTableComponent;

  onColumnConfigClick(event: Event): void {
    const target = event.target as HTMLElement;
    // Find the actual button element from PrimeNG p-button
    // p-button wraps a button element, so we need to find it
    const pButtonElement = target.closest('p-button');
    const buttonElement = pButtonElement
      ? (pButtonElement.querySelector('button') as HTMLElement)
      : (target.closest('button') as HTMLElement) || target;

    if (buttonElement && this.advancedTable) {
      this.advancedTable.openColumnSelector(buttonElement);
    }
  }
}
